import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@edusense/shared-auth-core';
import { DAON_USAGE_TEST_IDS } from '@edusense/shared-constants';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  DAON_BILLING_URL, addDaonInsider, applyDaonAdjustment, describeDaonError, getDaonAdjustments,
  getDaonBalance, getDaonInsiders, getDaonOverview, previewDaonAdjustment, removeDaonInsider, saveDaonPolicy,
  validateDaonAdjustment, validateDaonPolicy,
  type DaonAdjustmentInput, type DaonAdjustmentPreview, type DaonInsider, type DaonOverview, type DaonPolicy,
} from '../daon/daonAdminApi';

const integer = (value: number) => value.toLocaleString('ko-KR');
const usd = (value: string | null) => value === null ? '집계 대기' : new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(Number(value));
const dateTime = (value: string | null) => {
  if (value === null) return '동기화 대기';
  const timestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}Z` : value;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
};

function Message({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <p role={error ? 'alert' : 'status'} className={`rounded-lg border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-800' : 'border-blue-100 bg-blue-50 text-blue-900'}`}>{children}</p>;
}
function Stat({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs font-medium text-slate-500">{label}</p><p className="mt-2 break-words text-2xl font-semibold tabular-nums text-slate-900">{value}</p>{detail && <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>}</div>;
}

export function DaonUsageOverview({ overview }: { overview: DaonOverview }) {
  const { usage, policy, credits, sales } = overview;
  const budgetPercentage = overview.budgetCostUsd === null || Number(policy.monthlyBudgetUsd) <= 0
    ? null : Number(overview.budgetCostUsd) / Number(policy.monthlyBudgetUsd) * 100;
  return <>
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Stat label="월 예산 산정 비용" value={usd(overview.budgetCostUsd)} detail="확정 비용과 최근 사용량 추정 비용을 합친 판매 예산 기준" />
      <Stat label="확정 청구 비용" value={usage?.officialCostReportedThrough ? usd(usage.officialCostUsd) : '집계 대기'} detail={usage?.officialCostReportedThrough ? `비용 집계: ${dateTime(usage.officialCostReportedThrough)}까지` : 'Claude 확정 비용 동기화가 필요합니다.'} />
      <Stat label="월 예산 사용률" value={budgetPercentage === null ? '집계 대기' : `${budgetPercentage.toFixed(1)}%`} detail={`월 예산 ${usd(policy.monthlyBudgetUsd)} · 안전 준비금 ${usd(policy.safetyUsd)}`} />
      <Stat label="추가 판매 가능" value={sales.status === 'unavailable' ? '산정 대기' : `${integer(sales.remainingBeads)} 닢`} detail={sales.status === 'unavailable' ? '비용 또는 질문 원가 자료를 확인하세요.' : '기존 잔액·예약과 예산을 반영한 현재 판매량'} />
    </div>
    {sales.status === 'unavailable' && <Message>판매량 산정에 필요한 자료가 아직 준비되지 않았습니다. 상태: {sales.reason}. 마지막 비용 자료: {dateTime(sales.updatedAt)}</Message>}
    <Card>
      <CardHeader><CardTitle>Claude API 사용량</CardTitle><p className="text-sm text-slate-500">조직 전체 · UTC 월 기준. 연결된 Claude 조직의 사용량이며 다른 앱 사용분이 포함될 수 있습니다.</p></CardHeader>
      <CardContent className="space-y-5">
        {usage ? <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Stat label="입력 토큰" value={integer(usage.inputTokens)} detail="캐시 제외" />
            <Stat label="출력 토큰" value={integer(usage.outputTokens)} />
            <Stat label="캐시 읽기 토큰" value={integer(usage.cacheReadInputTokens)} />
            <Stat label="캐시 생성 토큰" value={integer(usage.cacheCreationInputTokens)} />
          </div>
          <p className="text-xs leading-6 text-slate-500">사용량 범위 {dateTime(usage.periodStart)} ~ {dateTime(usage.reportedThrough)} · 마지막 동기화 {dateTime(usage.observedAt)}<br />확정 비용 보고 범위: {usage.officialCostReportedThrough === null ? '동기화 대기' : `${dateTime(usage.officialCostReportedThrough)}까지`} · 화면 시각: 한국 시간<br />조직 ID {usage.organizationId}</p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table aria-label="Claude 일별 사용량" className="w-full min-w-[700px] text-right text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr><th scope="col" className="p-3 text-left">날짜 (UTC)</th><th scope="col" className="p-3">입력</th><th scope="col" className="p-3">출력</th><th scope="col" className="p-3">캐시 읽기</th><th scope="col" className="p-3">캐시 생성</th><th scope="col" className="p-3">확정 비용</th></tr></thead>
              <tbody>{usage.daily.length ? usage.daily.map((day) => <tr key={day.date} className="border-t border-slate-100 tabular-nums"><th scope="row" className="p-3 text-left font-normal">{day.date}</th><td className="p-3">{integer(day.inputTokens)}</td><td className="p-3">{integer(day.outputTokens)}</td><td className="p-3">{integer(day.cacheReadInputTokens)}</td><td className="p-3">{integer(day.cacheCreationInputTokens)}</td><td className="p-3">{usd(day.costUsd)}</td></tr>) : <tr><td colSpan={6} className="p-6 text-center text-slate-500">보고된 일별 사용량이 없습니다.</td></tr>}</tbody>
            </table>
          </div>
        </> : <Message>Claude 사용량 동기화 대기 중입니다. 토큰 소비와 확정 비용은 자료가 도착하면 표시됩니다.</Message>}
      </CardContent>
    </Card>
    <Card><CardHeader><CardTitle>닢 현황</CardTitle><p className="text-sm text-slate-500">Pay에 기록된 구매와 관리자 보정 내역 기준입니다.</p></CardHeader><CardContent>
      <dl className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">{([
        ['누적 발행', credits.issued], ['누적 사용', credits.consumed], ['누적 회수', credits.revoked], ['미사용 잔액', credits.outstanding], ['구매 처리 대기', credits.pending], ['구매 예약', credits.reserved], ['질문 사용 예약', credits.held],
      ] as const).map(([label, value]) => <div key={label}><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-2 text-xl font-semibold tabular-nums">{integer(value)} <span className="text-xs font-normal">닢</span></dd></div>)}</dl>
    </CardContent></Card>
  </>;
}

function PolicyEditor({ policy, token }: { policy: DaonPolicy; token: string | null | undefined }) {
  const client = useQueryClient();
  const [monthly, setMonthly] = useState(policy.monthlyBudgetUsd);
  const [safety, setSafety] = useState(policy.safetyUsd);
  const [limit, setLimit] = useState(policy.saleLimit === null ? '' : String(policy.saleLimit));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setMonthly(policy.monthlyBudgetUsd);
    setSafety(policy.safetyUsd);
    setLimit(policy.saleLimit === null ? '' : String(policy.saleLimit));
  }, [policy.monthlyBudgetUsd, policy.safetyUsd, policy.saleLimit, policy.revision]);
  const mutation = useMutation({
    mutationFn: () => saveDaonPolicy(token, { monthlyBudgetUsd: monthly, safetyUsd: safety, saleLimit: limit === '' ? null : Number(limit), expectedRevision: policy.revision, reason: reason.trim() }),
    onSuccess: async () => { setSaved(true); setReason(''); await client.invalidateQueries({ queryKey: ['daon-admin'] }); },
    onError: (cause) => { const described = describeDaonError(cause); setError(described.message); if (described.status === 409) void client.invalidateQueries({ queryKey: ['daon-admin'] }); },
  });
  function save(event: FormEvent) {
    event.preventDefault(); setSaved(false);
    const validation = validateDaonPolicy(monthly, safety, limit, reason); setError(validation);
    if (validation === null) mutation.mutate();
  }
  return <Card><CardHeader><CardTitle>예산·판매량 관리</CardTitle><p className="text-sm leading-6 text-slate-500">전체 닢 수량은 원장에 기록됩니다. 추가 판매량은 월 예산, 안전 준비금과 미사용 닢 상한으로 조정합니다.</p></CardHeader><CardContent>
    <form onSubmit={save} data-testid={DAON_USAGE_TEST_IDS.policy} className="space-y-4">
      <fieldset disabled={mutation.isPending} className="grid gap-4 md:grid-cols-3">
        <label className="space-y-2 text-sm font-medium">월 예산 (USD)<Input value={monthly} onChange={(event) => setMonthly(event.target.value)} inputMode="decimal" required /></label>
        <label className="space-y-2 text-sm font-medium">안전 준비금 (USD)<Input value={safety} onChange={(event) => setSafety(event.target.value)} inputMode="decimal" required /></label>
        <label className="space-y-2 text-sm font-medium">미사용 닢 상한<Input value={limit} onChange={(event) => setLimit(event.target.value)} inputMode="numeric" placeholder="제한 없음" /><span className="block text-xs font-normal leading-5 text-slate-500">잔액과 구매 예약 합계의 최대치. 비우면 예산만 적용합니다.</span></label>
        <label className="space-y-2 text-sm font-medium md:col-span-3">변경 사유<Input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="예: 이번 달 운영 예산 증액" required /></label>
      </fieldset>
      {error && <Message error>{error}</Message>}{saved && <Message>운영 정책을 저장했습니다.</Message>}
      <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? '저장 중…' : '운영 정책 저장'}</Button>
    </form>
  </CardContent></Card>;
}

function CreditAdjustment({ overview, token }: { overview: DaonOverview; token: string | null | undefined }) {
  const client = useQueryClient();
  const [userId, setUserId] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [direction, setDirection] = useState<'grant' | 'revoke'>('grant');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState<{ input: DaonAdjustmentInput; preview: DaonAdjustmentPreview } | null>(null);
  const balance = useQuery({ queryKey: ['daon-admin', token, 'balance', selectedId], queryFn: ({ signal }) => getDaonBalance(token, selectedId, signal), enabled: selectedId !== '', retry: false });
  const history = useQuery({ queryKey: ['daon-admin', token, 'adjustments', selectedId], queryFn: ({ signal }) => getDaonAdjustments(token, selectedId, signal), enabled: selectedId !== '', retry: false });
  const preview = useMutation({ mutationFn: (input: DaonAdjustmentInput) => previewDaonAdjustment(token, selectedId, input), onSuccess: (value, input) => setReviewed({ input, preview: value }), onError: (cause) => { setError(describeDaonError(cause).message); void client.invalidateQueries({ queryKey: ['daon-admin'] }); } });
  const apply = useMutation({
    mutationFn: (input: DaonAdjustmentInput) => applyDaonAdjustment(token, selectedId, input),
    onSuccess: async (value) => {
      setReviewed(null); setAmount(''); setReason('');
      setNotice(value.idempotentReplay ? '이미 반영된 보정 결과를 확인했습니다. 중복 지급·회수는 발생하지 않았습니다.' : `${value.adjustment.delta > 0 ? '지급을' : '회수를'} 반영했습니다. 잔액 ${integer(value.balance.balance)} 닢`);
      await client.invalidateQueries({ queryKey: ['daon-admin'] });
    },
    onError: (cause) => {
      const described = describeDaonError(cause); setError(described.message);
      if (described.status === 409) { setReviewed(null); void client.invalidateQueries({ queryKey: ['daon-admin'] }); }
    },
  });
  const busy = preview.isPending || apply.isPending;
  const failedApply = apply.isError ? describeDaonError(apply.error) : null;
  const retryUncertainOperation = reviewed !== null && apply.variables?.operationId === reviewed.input.operationId
    && failedApply !== null && (failedApply.status === 0 || failedApply.status >= 500);
  const validReview = reviewed !== null && reviewed.preview.userId === selectedId && reviewed.preview.allowed
    && reviewed.preview.operationId === reviewed.input.operationId && reviewed.preview.delta === reviewed.input.delta
    && reviewed.preview.reason === reviewed.input.reason && reviewed.preview.policyRevision === reviewed.input.expectedPolicyRevision
    && (retryUncertainOperation || (reviewed.input.expectedPolicyRevision === overview.policy.revision
      && reviewed.input.expectedBalance === balance.data?.balance && reviewed.input.expectedHeld === balance.data?.held));
  function lookup(event: FormEvent) {
    event.preventDefault(); if (!userId.trim()) return;
    setReviewed(null); setError(null); setNotice(null);
    if (selectedId === userId.trim()) void client.invalidateQueries({ queryKey: ['daon-admin', token] });
    else setSelectedId(userId.trim());
  }
  function review(event: FormEvent) {
    event.preventDefault(); setReviewed(null); setNotice(null);
    const validation = validateDaonAdjustment(amount, reason); setError(validation);
    if (validation || !balance.data) return;
    preview.mutate({ operationId: crypto.randomUUID(), delta: (direction === 'grant' ? 1 : -1) * Number(amount), reason: reason.trim(), expectedBalance: balance.data.balance, expectedHeld: balance.data.held, expectedPolicyRevision: overview.policy.revision });
  }
  function changed() { setReviewed(null); setError(null); setNotice(null); }
  return <Card><CardHeader><CardTitle>사용자 닢 지급·회수</CardTitle><p className="text-sm leading-6 text-slate-500">계정 화면의 사용자 ID로 조회하세요. 사유와 처리 관리자를 이력에 남기며, 질문 사용 예약 닢은 회수할 수 없습니다.</p></CardHeader><CardContent className="space-y-5">
    <form onSubmit={lookup} data-testid={DAON_USAGE_TEST_IDS.userLookup} className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <label className="flex-1 space-y-2 text-sm font-medium">사용자 ID<Input value={userId} onChange={(event) => setUserId(event.target.value)} disabled={busy} autoComplete="off" required /></label>
      <Button type="submit" variant="outline" disabled={busy || balance.isFetching}>{balance.isFetching ? '조회 중…' : '사용자 조회'}</Button>
    </form>
    {balance.error && <Message error>{describeDaonError(balance.error).message}</Message>}
    {balance.data && <>
      <p className="break-all text-sm text-slate-500">조회한 사용자: <span className="font-medium text-slate-800">{balance.data.userId}</span></p>
      <dl className="grid grid-cols-3 gap-3 rounded-xl bg-slate-50 p-4">{([['전체 잔액', balance.data.balance], ['사용 가능', balance.data.available], ['질문 사용 예약', balance.data.held]] as const).map(([label, value]) => <div key={label}><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 text-lg font-semibold tabular-nums">{integer(value)} 닢</dd></div>)}</dl>
      <form onSubmit={review} data-testid={DAON_USAGE_TEST_IDS.adjustment} className="space-y-4">
        <fieldset disabled={busy || balance.isFetching} className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-2 text-sm font-medium">보정 종류<select value={direction} onChange={(event) => { setDirection(event.target.value as 'grant' | 'revoke'); changed(); }} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="grant">닢 지급</option><option value="revoke">닢 회수</option></select></label>
          <label className="space-y-2 text-sm font-medium">보정 수량 (닢)<Input value={amount} onChange={(event) => { setAmount(event.target.value); changed(); }} inputMode="numeric" required /></label>
          <label className="space-y-2 text-sm font-medium sm:col-span-2">지급·회수 사유<Input value={reason} onChange={(event) => { setReason(event.target.value); changed(); }} maxLength={500} required placeholder="예: 고객 문의 확인 후 사용 실패분 복구" /></label>
        </fieldset>
        <Button type="submit" variant="outline" disabled={busy || balance.isFetching} data-testid={DAON_USAGE_TEST_IDS.preview}>{preview.isPending ? '계산 중…' : '보정 결과 미리보기'}</Button>
      </form>
    </>}
    {error && <Message error>{error}</Message>}{notice && <Message>{notice}</Message>}
    {reviewed && <div className="space-y-4 rounded-xl border border-blue-200 bg-blue-50/50 p-4">
      <h3 className="font-semibold text-slate-900">적용 전 확인</h3>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div><dt className="text-slate-500">사용자 잔액</dt><dd className="mt-1 font-semibold">{integer(reviewed.preview.before.balance)} → {integer(reviewed.preview.after.balance)} 닢</dd></div>
        <div><dt className="text-slate-500">사용자 사용 가능 닢</dt><dd className="mt-1 font-semibold">{integer(reviewed.preview.before.available)} → {integer(reviewed.preview.after.available)} 닢</dd></div>
        <div><dt className="text-slate-500">질문 사용 예약 닢</dt><dd className="mt-1">{integer(reviewed.preview.before.held)} → {integer(reviewed.preview.after.held)} 닢</dd></div>
        <div><dt className="text-slate-500">전체 추가 판매 가능 닢</dt><dd className="mt-1 font-semibold">{integer(reviewed.preview.remainingBeadsBefore)} → {integer(reviewed.preview.remainingBeadsAfter)} 닢</dd></div>
        <div className="sm:col-span-2"><dt className="text-slate-500">처리 사유</dt><dd className="mt-1 break-words">{reviewed.preview.reason}</dd></div>
      </dl>
      {!reviewed.preview.allowed && <Message error>현재 조건에서는 보정할 수 없습니다. 상태: {reviewed.preview.reasonCode}</Message>}
      {reviewed.preview.allowed && !validReview && <Message error>잔액 또는 운영 정책이 변경되었습니다. 미리보기를 다시 확인하세요.</Message>}
      <Button type="button" disabled={busy || !validReview} onClick={() => { if (reviewed && validReview) { setError(null); apply.mutate(reviewed.input); } }} data-testid={DAON_USAGE_TEST_IDS.apply}>{apply.isPending ? '적용 중…' : '확인한 보정 적용'}</Button>
    </div>}
    {selectedId && <section aria-labelledby="daon-adjustment-history" data-testid={DAON_USAGE_TEST_IDS.history} className="space-y-3">
      <h3 id="daon-adjustment-history" className="font-semibold">관리자 보정 이력 <span className="text-xs font-normal text-slate-500">최근 100건</span></h3>
      {history.isPending ? <p role="status" className="text-sm text-slate-500">이력을 불러오는 중…</p> : history.error ? <Message error>{describeDaonError(history.error).message}</Message> : history.data?.length ? <div className="overflow-x-auto rounded-lg border border-slate-200"><table aria-label="사용자 닢 보정 이력" className="w-full min-w-[640px] text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th scope="col" className="p-3">처리 시각</th><th scope="col" className="p-3">수량</th><th scope="col" className="p-3">처리 후 잔액</th><th scope="col" className="p-3">사유</th><th scope="col" className="p-3">처리 관리자</th></tr></thead><tbody>{history.data.map((row) => <tr key={row.operationId} className="border-t border-slate-100"><td className="whitespace-nowrap p-3">{dateTime(row.createdAt)}</td><td className="whitespace-nowrap p-3 font-medium tabular-nums">{row.delta > 0 ? '+' : ''}{integer(row.delta)} 닢</td><td className="p-3 tabular-nums">{integer(row.balanceAfter)} 닢</td><td className="max-w-80 break-words p-3">{row.reason}</td><td className="break-all p-3">{row.actor}</td></tr>)}</tbody></table></div> : <p className="text-sm text-slate-500">관리자 보정 이력이 없습니다.</p>}
    </section>}
  </CardContent></Card>;
}

function InsiderFreeAccessCard({ token }: { token: string | null | undefined }) {
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const insiders = useQuery({
    queryKey: ['daon-admin', token, 'insiders'],
    queryFn: ({ signal }) => getDaonInsiders(token, signal),
    retry: false,
  });
  const addMutation = useMutation({
    mutationFn: (value: string) => addDaonInsider(token, value),
    onSuccess: async (_data, value) => {
      setEmail('');
      setError(null);
      setNotice(`${value.trim().toLowerCase()} 계정을 다온 사주 무료 이용 내부자로 등록했습니다.`);
      await client.invalidateQueries({ queryKey: ['daon-admin', token, 'insiders'] });
    },
    onError: (cause) => {
      setNotice(null);
      setError(describeDaonError(cause).message);
    },
  });
  const removeMutation = useMutation({
    mutationFn: (target: string) => removeDaonInsider(token, target),
    onSuccess: async (_data, target) => {
      setError(null);
      setNotice(`${target} 계정을 무료 이용 내부자 목록에서 제외했습니다.`);
      await client.invalidateQueries({ queryKey: ['daon-admin', token, 'insiders'] });
    },
    onError: (cause) => {
      setNotice(null);
      setError(describeDaonError(cause).message);
    },
  });
  const busy = addMutation.isPending || removeMutation.isPending;
  function handleAdd(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('유효한 이메일 주소를 입력하세요.');
      return;
    }
    addMutation.mutate(trimmed);
  }
  return <Card data-testid="daon-insider-free-card">
    <CardHeader>
      <CardTitle>내부자 무료 이용 설정 (외부 사용자 유료 전용)</CardTitle>
      <p className="text-sm leading-6 text-slate-500">
        다온 사주(saju.ugot.uk)는 외부 사용자에게 닢(구슬) 결제를 강제하며, 아래에 등록된 내부자 이메일만 닢 차감 없이 무료로 질문할 수 있습니다.
      </p>
    </CardHeader>
    <CardContent className="space-y-4">
      <form onSubmit={handleAdd} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex-1 space-y-2 text-sm font-medium">
          내부자 이메일 추가
          <Input
            type="email"
            placeholder="예: dudnjsckrgo@gmail.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
            required
          />
        </label>
        <Button type="submit" disabled={busy || !email.trim()}>{addMutation.isPending ? '추가 중…' : '내부자 추가'}</Button>
      </form>
      {error && <Message error>{error}</Message>}
      {notice && <Message>{notice}</Message>}
      {insiders.isPending ? (
        <p role="status" className="text-sm text-slate-500">내부자 목록을 불러오는 중…</p>
      ) : insiders.error ? (
        <Message error>{describeDaonError(insiders.error).message}</Message>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table aria-label="다온 사주 무료 이용 내부자 목록" className="w-full min-w-[480px] text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th scope="col" className="p-3">이메일</th>
                <th scope="col" className="p-3">등록자</th>
                <th scope="col" className="p-3">등록 시각</th>
                <th scope="col" className="w-24 p-3 text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {insiders.data?.length ? insiders.data.map((row: DaonInsider) => (
                <tr key={row.email} className="border-t border-slate-100">
                  <td className="p-3 font-mono font-medium text-slate-900">{row.email}</td>
                  <td className="p-3 text-slate-500">{row.addedBy ?? '-'}</td>
                  <td className="p-3 text-slate-500">{row.createdAt ? dateTime(row.createdAt) : '-'}</td>
                  <td className="p-3 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => removeMutation.mutate(row.email)}
                    >
                      제외
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-slate-500">등록된 무료 이용 내부자가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>;
}

export default function DaonUsageCreditsPage() {
  const { token } = useAuth();
  const overview = useQuery({ queryKey: ['daon-admin', token, 'overview'], queryFn: ({ signal }) => getDaonOverview(token, signal), retry: false, refetchInterval: 60_000 });
  return <section data-testid={DAON_USAGE_TEST_IDS.page} className="space-y-6">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">다온 사용량·닢</h1><p className="mt-2 text-sm leading-6 text-slate-500">내부자 무료 이용 계정을 관리하고, 외부 사용자 유료 닢 공급량과 운영 예산을 관리합니다.</p></div>
      <Button type="button" variant="outline" onClick={() => void overview.refetch()} disabled={overview.isFetching} data-testid={DAON_USAGE_TEST_IDS.refresh}><RefreshCw className={`mr-2 h-4 w-4 ${overview.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />새로고침</Button>
    </div>
    <InsiderFreeAccessCard token={token} />
    {overview.isPending ? <p role="status" className="py-12 text-center text-sm text-slate-500">사용량과 닢 정보를 불러오는 중…</p> : overview.error ? <Message error>{describeDaonError(overview.error).message}</Message> : overview.data && <>
      <DaonUsageOverview overview={overview.data} />
      <PolicyEditor policy={overview.data.policy} token={token} />
      <CreditAdjustment overview={overview.data} token={token} />
    </>}
    <Card><CardHeader><CardTitle>Claude 자동 충전</CardTitle></CardHeader><CardContent className="space-y-3"><p className="text-sm leading-6 text-slate-600">API 잔액 부족 시 자동 결제·충전은 Claude Console의 Billing에서 설정합니다. 월 예산은 다온의 판매량을 산정하는 기준이며 Claude의 실제 충전 한도와 별개입니다.</p><a href={DAON_BILLING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-slate-50">Claude Console에서 자동 충전 설정<ExternalLink className="h-4 w-4" aria-hidden="true" /><span className="sr-only">새 창에서 열림</span></a></CardContent></Card>
  </section>;
}
